import React, { forwardRef, useEffect, useState } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { companiesService } from '../../services/companiesService';
import { CompanyShortDto } from '../../models/companies/companyShortDto';

interface CompanyAutocompleteProps {
    company: CompanyShortDto | null; // Required {id,name} object, not just id, to not query name from backend
    onCompanyChange: (newCompany: CompanyShortDto | null) => void;
    label?: string;
    style?: React.CSSProperties;
}

const CompanyAutocomplete = forwardRef<HTMLDivElement, CompanyAutocompleteProps>(({ company, onCompanyChange, label = "Клиент", style = {} }, ref) => {
    const [companies, setCompanies] = useState<CompanyShortDto[]>([]);
    const [initialCompanies, setInitialCompanies] = useState<CompanyShortDto[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        setLoading(true);
        companiesService.search({
            name: '',
            page: 0,
            pageSize: 10,
        })
            .then(response => {
                setCompanies(response.data.data.items);
                setInitialCompanies(response.data.data.items); // Save the initial list of companies
            })
            .catch(error => {
                console.error('Error fetching companies:', error);
            })
            .finally(() => setLoading(false));
    }, []);

    const handleInputChange = (_: React.SyntheticEvent, newInputValue: string) => {
        if (newInputValue.length >= 3) {
            setLoading(true);
            companiesService.search({
                name: newInputValue,
                page: 0,
                pageSize: 10,
            })
                .then(response => {
                    // Always use the search results, even if empty
                    setCompanies(response.data.data.items);
                })
                .catch(error => {
                    console.error('Error fetching companies:', error);
                    // On error, revert to initial companies
                    setCompanies(initialCompanies);
                })
                .finally(() => setLoading(false));
        } else {
            setCompanies(initialCompanies); // Revert to initial list if input is cleared or less than 3 chars
        }
    };

    const handleChange = (_: React.SyntheticEvent, newValue: CompanyShortDto | null) => {
        onCompanyChange(newValue);
    };

    return (
        <Autocomplete
            options={companies}
            getOptionLabel={(option) => option.name}
            value={company}
            onChange={handleChange}
            onInputChange={handleInputChange}
            noOptionsText="Не найдено"
            isOptionEqualToValue={(option, value) => value == null ? false : option.id === value.id}
            loading={loading}
            loadingText="Поиск..."
            ref={ref}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    variant="outlined"
                    margin="dense"
                    size="small"
                    style={style}
                    placeholder="Введите от 3х букв"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
);

export default CompanyAutocomplete;
